import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Modal, useAsync, useSession, useToast } from '../ui.jsx';
import { timestampLabel, unitName } from '../lib/format.js';

const ROLE_LABELS = { admin: '管理員', member: '一般成員' };
const STATUS_LABELS = { active: '已啟用', invited: '已邀請，待登入', disabled: '已停用' };

/**
 * Membership is invitation-only: an administrator pre-creates the membership with
 * its initial role, and the Clerk identity is linked to it on first sign-in.
 * Self-demotion is refused and at least one 管理員 always remains.
 */
export default function MembersPage() {
  const session = useSession();
  const notify = useToast();
  const members = useAsync(() => api.members(), []);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState(null);

  if (members.loading) return <Loading />;
  if (members.error) return <ErrorBanner error={members.error} />;

  const changeRole = async (member, role) => {
    setError(null);
    try {
      await api.changeRole(member.id, { role });
      notify(`${member.display_name} 已變更為${ROLE_LABELS[role]}`);
      members.reload();
      session.reloadSession();
    } catch (failure) {
      setError(failure);
    }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台</div>
          <h1>成員與角色</h1>
          <p className="small muted">沒有公開註冊：管理員邀請成員並指定初始角色，所有變更都會留痕。</p>
        </div>
        <button type="button" className="primary" onClick={() => setInviting(true)} data-testid="member-invite">邀請成員</button>
      </div>

      <ErrorBanner error={error} />

      <Card testId="member-list">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>成員</th>
                <th>電郵</th>
                <th>角色</th>
                <th>狀態</th>
                <th>可存取單位</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.data.members.map((member) => {
                const isSelf = member.id === session.member.id;
                const lastAdmin = member.role === 'admin' && members.data.admin_count <= 1;
                return (
                  <tr key={member.id} data-testid={`member-${member.display_name}`}>
                    <td>
                      <strong>{member.display_name}</strong>
                      {isSelf ? <span className="chip" style={{ marginLeft: 6 }}>我</span> : null}
                    </td>
                    <td className="small">{member.email}</td>
                    <td data-testid={`member-role-${member.display_name}`}>{ROLE_LABELS[member.role]}</td>
                    <td className="small">
                      {STATUS_LABELS[member.status]}
                      {member.auth_subject
                        ? <div className="muted" data-testid={`member-linked-${member.display_name}`}>已連結登入身分</div>
                        : <div className="muted">尚未連結登入身分</div>}
                      {member.activated_at ? <div className="muted">{timestampLabel(member.activated_at)}</div> : null}
                    </td>
                    <td className="small">{member.unit_ids.map((unitId) => unitName(members.data.units, unitId)).join('、')}</td>
                    <td>
                      {member.role === 'admin' ? (
                        <button
                          type="button"
                          className="small"
                          disabled={isSelf || lastAdmin}
                          title={isSelf ? '不能降級自己' : lastAdmin ? '必須保留至少一名管理員' : ''}
                          data-testid={`demote-${member.display_name}`}
                          onClick={() => changeRole(member, 'member')}
                        >
                          降為一般成員
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="small"
                          data-testid={`promote-${member.display_name}`}
                          onClick={() => changeRole(member, 'admin')}
                        >
                          升為管理員
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="small muted" style={{ marginTop: 10 }}>
          管理員不能降級自己，也不能令合租群組少於一名管理員。角色變更後，該成員的下一次請求即使用新權限。
        </p>
      </Card>

      {inviting ? (
        <InviteModal
          units={members.data.units}
          onClose={() => setInviting(false)}
          onDone={() => { setInviting(false); members.reload(); }}
        />
      ) : null}
    </div>
  );
}

function InviteModal({ units, onClose, onDone }) {
  const notify = useToast();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [unitIds, setUnitIds] = useState(() => units.map((unit) => unit.id));
  const [error, setError] = useState(null);

  return (
    <Modal title="邀請成員" subtitle="Restricted invitation-only：先建立 membership，成員登入後自動連結身分" onClose={onClose}>
      <ErrorBanner error={error} />
      <div className="stack">
        <label className="field"><span>成員名稱</span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} data-testid="invite-name" /></label>
        <label className="field"><span>電郵</span><input value={email} onChange={(event) => setEmail(event.target.value)} data-testid="invite-email" /></label>
        <label className="field">
          <span>初始角色</span>
          <select value={role} onChange={(event) => setRole(event.target.value)} data-testid="invite-role">
            <option value="member">一般成員</option>
            <option value="admin">管理員</option>
          </select>
        </label>
        <fieldset>
          <legend>可存取單位</legend>
          {units.map((unit) => (
            <label className="row small" key={unit.id} style={{ gap: 6 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={unitIds.includes(unit.id)}
                data-testid={`invite-unit-${unit.name}`}
                onChange={(event) => setUnitIds(event.target.checked
                  ? [...unitIds, unit.id]
                  : unitIds.filter((id) => id !== unit.id))}
              />
              {unit.name}
            </label>
          ))}
        </fieldset>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={!displayName.trim() || !email.trim() || !unitIds.length}
          data-testid="invite-submit"
          onClick={async () => {
            try {
              const result = await api.inviteMember({
                display_name: displayName.trim(),
                email: email.trim(),
                role,
                unit_ids: unitIds,
              });
              notify(result.clerk_invitation.status === 'created'
                ? '已建立 membership 及 Clerk 邀請'
                : '已建立 membership（本機未設定 Clerk secret，未發出 Clerk 邀請）');
              onDone();
            } catch (failure) {
              setError(failure);
            }
          }}
        >
          建立邀請
        </button>
      </div>
    </Modal>
  );
}
